/**
 * Profile Extraction Schemas
 *
 * Zod schemas for AI-powered profile extraction from documents.
 * Used by Brain service to validate LLM output (anti-hallucination)
 * and by Platform API / frontend for type safety.
 *
 * Design:
 * - Every field is nullable: the AI must not fabricate missing data
 * - Arrays default to []: a missing section means "no entries found"
 * - CEFR levels constrained to valid enum values
 * - Dates in YYYY-MM or YYYY-MM-DD format (loose: the AI may only find a year)
 */

import { z } from 'zod';

// ═══════════════════════════════════════════════════════════════════════
// NESTED SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

export const extractedExperienceSchema = z.object({
  company: z.string(),
  role: z.string(),
  startDate: z.string().nullable().default(null),
  endDate: z.string().nullable().default(null),
  description: z.string().nullable().default(null),
  location: z
    .object({
      city: z.string().nullable().default(null),
      country: z.string().nullable().default(null),
    })
    .nullable()
    .default(null),
});

export const extractedEducationSchema = z.object({
  institution: z.string(),
  degree: z.string().nullable().default(null),
  field: z.string().nullable().default(null),
  startDate: z.string().nullable().default(null),
  endDate: z.string().nullable().default(null),
});

export const cefrLevelSchema = z.enum(['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'native']);

export const extractedLanguageSchema = z.object({
  name: z.string(),
  level: cefrLevelSchema.nullable().default(null),
});

export const extractedCertificationSchema = z.object({
  name: z.string(),
  authority: z.string().nullable().default(null),
  issueDate: z.string().nullable().default(null),
});

export const extractedWebsiteUrlSchema = z.object({
  type: z.string(),
  url: z.string(),
});

// ═══════════════════════════════════════════════════════════════════════
// MAIN EXTRACTION SCHEMA
// ═══════════════════════════════════════════════════════════════════════

export const extractedProfileSchema = z.object({
  firstName: z.string().nullable().default(null),
  lastName: z.string().nullable().default(null),
  headline: z.string().nullable().default(null),
  summary: z.string().nullable().default(null),
  currentRole: z.string().nullable().default(null),
  experience: z.array(extractedExperienceSchema).default([]),
  education: z.array(extractedEducationSchema).default([]),
  skills: z.array(z.string()).default([]),
  languages: z.array(extractedLanguageSchema).default([]),
  certifications: z.array(extractedCertificationSchema).default([]),
  websiteUrls: z.array(extractedWebsiteUrlSchema).default([]),
});

export type ExtractedProfileData = z.infer<typeof extractedProfileSchema>;
export type ExtractedExperience = z.infer<typeof extractedExperienceSchema>;
export type ExtractedEducation = z.infer<typeof extractedEducationSchema>;
export type ExtractedLanguage = z.infer<typeof extractedLanguageSchema>;
export type ExtractedCertification = z.infer<typeof extractedCertificationSchema>;

// ═══════════════════════════════════════════════════════════════════════
// REQUEST / RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

export const extractProfileSourceSchema = z.enum(['linkedin_pdf', 'cv', 'other']);

export type ExtractProfileSource = z.infer<typeof extractProfileSourceSchema>;

export const extractProfileRequestSchema = z.object({
  rawText: z.string().min(1),
  source: extractProfileSourceSchema.default('other'),
  fileName: z.string().optional(),
});

export type ExtractProfileRequest = z.infer<typeof extractProfileRequestSchema>;

export const extractProfileResponseSchema = z.object({
  data: extractedProfileSchema,
  source: extractProfileSourceSchema,
  extractedAt: z.string(),
  /** Whether AI extraction was used (vs. regex fallback) */
  aiPowered: z.boolean(),
});

export type ExtractProfileResponse = z.infer<typeof extractProfileResponseSchema>;
